import axios from 'axios';
import qs from 'qs';
import { store } from 'src/redux';
import { makeLogout } from 'src/utils/auth';
import { getAccessToken } from 'src/utils/tokenService';

const log = (message: string, data?: unknown) => {
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  console.log(`[Axios ${timestamp}] ${message}`, data !== undefined ? data : '');
};

const stringifyConfig = {
  arrayFormat: 'brackets' as const,
};

export const stringify = (data: Record<string, any>): string => {
  const cleanedData = Object.keys(data).reduce<Record<string, any>>((acc, key) => {
    if (data[key] !== undefined && data[key] !== null) {
      acc[key] = data[key];
    }
    return acc;
  }, {});

  return qs.stringify(cleanedData);
};

export const encodeQueryParams = (queryParameters: Record<string, any> | null | undefined): string => {
  const queryParams =
    queryParameters && Object.keys(queryParameters).length ? qs.stringify(queryParameters, stringifyConfig) : null;

  return queryParams ? `?${queryParams}` : '';
};

export const encodeBody = (body: any[] | Record<string, any> | null | undefined): string | undefined => {
  if (body && !(body instanceof Array) && !Object.keys(body).length) {
    return undefined;
  }
  return JSON.stringify(body);
};

axios.defaults.baseURL = process.env.BASE_URL || '/';

let axiosRequestCount = 0;

axios.interceptors.request.use(async (request) => {
  axiosRequestCount++;
  const reqNum = axiosRequestCount;
  log(`📤 Request #${reqNum}`, { url: request.url, method: request.method });

  const token = await getAccessToken();

  log(`📤 Request #${reqNum} got token`, { hasToken: !!token });

  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  request.withCredentials = true;
  return request;
});

axios.interceptors.response.use(
  (response) => {
    log(`✅ Response`, { url: response.config.url, status: response.status });
    return response;
  },
  (err) => {
    log(`❌ Response Error`, {
      url: err.config?.url,
      status: err.response?.status,
      data: err.response?.data,
    });

    if (err.response) {
      const status = err.response.status;
      const originalUrl = window.location.href;

      if (status === 401) {
        log('🚨 401 Unauthorized - triggering logout');
        makeLogout();
      } else if (status === 503) {
        store.dispatch({ type: 'app/setMaintenanceMode', payload: true });
      } else if (status === 409) {
        const { workspace_id, workspace_name } = err.response.data;
        window.location.href = `/error?status=409&workspace_id=${workspace_id}&workspace_name=${workspace_name}&redirect_url=${encodeURIComponent(
          originalUrl,
        )}`;
      } else if (status === 403) {
        log('⚠️ 403 Forbidden', { data: err.response.data });
        const { workspace_id } = err.response.data;
        window.location.href = `/error?status=403&workspace_id=${workspace_id || ''}&redirect_url=${encodeURIComponent(
          originalUrl,
        )}`;
      } else if (status === 404) {
        window.location.href = `/error?status=404&redirect_url=${encodeURIComponent(originalUrl)}`;
      }
    }

    return Promise.reject({ ...err.response?.data, status: err.response?.status });
  },
);
